import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward, X, Sun, Sparkle, Shuffle, Hourglass, MoonStars } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { VolumeControl } from "../components/VolumeControl";
import { useAudioStabilizer } from "../lib/useAudioStabilizer";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const SILENT_DURATION_MS = 9000;  // when a memory has no audio narration

export default function ReminiscePlayer() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const mode = search.get("mode") || "full";
    const theme = search.get("theme") || null;
    const era = search.get("era") || null;
    const [profile, setProfile] = useState(null);
    const [playlist, setPlaylist] = useState([]);
    const [themeLabel, setThemeLabel] = useState(null);
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [loading, setLoading] = useState(true);

    const audioRef = useRef(null);
    const silentTimerRef = useRef(null);
    const { volume, preset, setVolume, setPreset } = useAudioStabilizer(audioRef, audioUrl);

    // Load the playlist once (with the chosen mode/theme/era)
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const params = {};
                if (mode && mode !== "full") params.mode = mode;
                if (theme) params.theme = theme;
                if (era)   params.era   = era;
                const [{ data }, themesRes] = await Promise.all([
                    api.get(`/profiles/${id}/playlist`, { params }),
                    theme ? api.get(`/session-themes`).catch(() => ({ data: { themes: [] } })) : Promise.resolve({ data: { themes: [] } }),
                ]);
                if (!active) return;
                setProfile(data.profile);
                setPlaylist(data.playlist || []);
                if (theme) {
                    const t = (themesRes.data.themes || []).find((x) => x.slug === theme);
                    setThemeLabel(t?.label || null);
                }
            } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load.");
                navigate(`/profiles/${id}/reminisce`);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [id, navigate, mode, theme, era]);

    const current = playlist[idx];

    // Load photo + audio blobs for the current memory
    useEffect(() => {
        if (!current) return;
        let cancelled = false;
        let urls = [];
        setPhotoUrl(null);
        setAudioUrl(null);
        (async () => {
            if (current.photo_file_id) {
                try {
                    const r = await api.get(`/files/${current.photo_file_id}/download`, { responseType: "blob" });
                    if (cancelled) return;
                    const u = URL.createObjectURL(r.data);
                    urls.push(u);
                    setPhotoUrl(u);
                } catch { /* skip */ }
            }
            if (current.audio_file_id) {
                try {
                    const r = await api.get(`/files/${current.audio_file_id}/download`, { responseType: "blob" });
                    if (cancelled) return;
                    const u = URL.createObjectURL(r.data);
                    urls.push(u);
                    setAudioUrl(u);
                } catch { /* skip */ }
            }
        })();
        return () => {
            cancelled = true;
            urls.forEach((u) => URL.revokeObjectURL(u));
        };
    }, [current]);

    const advance = useCallback(() => {
        setIdx((i) => (i < playlist.length - 1 ? i + 1 : i));
    }, [playlist.length]);

    const goBack = useCallback(() => {
        setIdx((i) => (i > 0 ? i - 1 : 0));
    }, []);

    // Auto-advance: when audio ends OR after SILENT_DURATION_MS if no audio
    useEffect(() => {
        if (paused || !current) return;
        if (silentTimerRef.current) { clearTimeout(silentTimerRef.current); silentTimerRef.current = null; }
        if (audioUrl && audioRef.current) {
            // play() may reject if browser blocks autoplay; ignore failure (user can press play)
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => { /* user will press play */ });
        } else {
            silentTimerRef.current = setTimeout(advance, SILENT_DURATION_MS);
        }
        return () => {
            if (silentTimerRef.current) clearTimeout(silentTimerRef.current);
        };
    }, [audioUrl, paused, advance, current]);

    // Pause / resume hooks
    useEffect(() => {
        if (!audioRef.current) return;
        if (paused) audioRef.current.pause();
        else audioRef.current.play().catch(() => {});
    }, [paused]);

    if (loading || !current) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#1a1c25]" data-testid="player-loading">
                <ForgetMeNotIcon size={48} className="animate-pulse" />
            </div>
        );
    }

    const atEnd = idx >= playlist.length - 1;

    return (
        <div className="min-h-screen bg-[#1a1c25] text-white relative overflow-hidden flex flex-col" data-testid="reminisce-player">
            {/* Carer exit — small, top-left */}
            <Link
                to={`/profiles/${id}/reminisce`}
                className="absolute top-4 left-4 z-30 inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors px-3 py-2"
                data-testid="player-exit"
            >
                <X size={16} /> end
            </Link>

            {/* Active session-mode badge — center-top, only when not "full" */}
            {mode !== "full" && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30" data-testid="player-mode-badge">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[11px] uppercase tracking-[0.22em] text-white/85">
                        {mode === "today" && (<><Sun size={13} weight="fill" className="text-[#F4D068]" /> Today&apos;s session</>)}
                        {mode === "themed" && (<><Sparkle size={13} weight="fill" className="text-[#A8B5E0]" /> Themed · {themeLabel || theme}</>)}
                        {mode === "shuffle" && (<><Shuffle size={13} weight="fill" className="text-[#A8B5E0]" /> Shuffle</>)}
                        {mode === "era" && (<><Hourglass size={13} weight="fill" className="text-[#D4C9B0]" /> The {era}</>)}
                        {mode === "lights-on" && (<><MoonStars size={13} weight="fill" className="text-[#A8B5E0]" /> Lights On · Calm session</>)}
                    </div>
                </div>
            )}

            {/* Progress dots */}
            <div className="absolute top-4 right-4 z-30 flex gap-1.5" data-testid="player-progress">
                {playlist.slice(0, 30).map((_, i) => (
                    <span key={i} className={`h-1 rounded-full transition-all ${i < idx ? "w-4 bg-white/40" : i === idx ? "w-8 bg-white" : "w-4 bg-white/15"}`} />
                ))}
                {playlist.length > 30 && <span className="ml-2 text-[10px] text-white/50">+{playlist.length - 30}</span>}
            </div>

            {/* Photo */}
            <div className="flex-1 flex items-center justify-center p-4">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={current.id}
                        initial={{ opacity: 0, scale: 1.02 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.99 }}
                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="w-full max-w-4xl"
                    >
                        {photoUrl ? (
                            <img
                                src={photoUrl}
                                alt={current.title}
                                className="w-full max-h-[60vh] object-contain rounded-2xl shadow-2xl mx-auto"
                                data-testid="player-photo"
                            />
                        ) : (
                            <div className="w-full aspect-video bg-gradient-to-br from-[#2a3450] to-[#1a1c25] rounded-2xl flex items-center justify-center" data-testid="player-photo-placeholder">
                                <ForgetMeNotIcon size={88} />
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Title + story + contributor */}
            <div className="px-6 sm:px-10 pb-32 max-w-4xl mx-auto w-full text-center">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={current.id + "-text"}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                    >
                        <p className="text-xs uppercase tracking-[0.3em] text-[#A8B5E0]/80 mb-2" data-testid="player-category">
                            {current.category_slug.replace(/-/g, " ")}
                        </p>
                        <h2 className="font-serif text-2xl sm:text-4xl text-white leading-snug" data-testid="player-title">
                            {current.title}
                        </h2>
                        {current.story && (
                            <p className="mt-4 text-white/80 leading-relaxed text-sm sm:text-base max-w-2xl mx-auto">
                                {current.story}
                            </p>
                        )}
                        {current.contributor_name && (
                            <p className="mt-4 text-[#A8B5E0] italic text-sm" data-testid="player-contributor">
                                — {current.contributor_name}
                            </p>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Audio (hidden — controlled programmatically) */}
            {audioUrl && (
                <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={advance}
                    data-testid="player-audio"
                />
            )}

            {/* Giant tablet-friendly controls */}
            <div className="absolute bottom-0 inset-x-0 z-30 bg-gradient-to-t from-[#1a1c25] via-[#1a1c25]/80 to-transparent pt-12 pb-6">
                <div className="flex items-center justify-center gap-4 sm:gap-8">
                    <button
                        onClick={goBack}
                        disabled={idx === 0}
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center backdrop-blur transition-all hover:-translate-y-0.5"
                        data-testid="player-back"
                        aria-label="Previous memory"
                    >
                        <SkipBack size={28} weight="fill" />
                    </button>
                    <button
                        onClick={() => setPaused((p) => !p)}
                        className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white text-[#4A5A95] hover:bg-[#F4F6FB] shadow-rem-glow flex items-center justify-center transition-all hover:-translate-y-0.5"
                        data-testid="player-toggle"
                        aria-label={paused ? "Resume" : "Pause"}
                    >
                        {paused ? <Play size={40} weight="fill" /> : <Pause size={40} weight="fill" />}
                    </button>
                    <button
                        onClick={advance}
                        disabled={atEnd}
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 flex items-center justify-center backdrop-blur transition-all hover:-translate-y-0.5"
                        data-testid="player-next"
                        aria-label="Next memory"
                    >
                        <SkipForward size={28} weight="fill" />
                    </button>
                </div>
                <div className="mt-5 flex justify-center px-4">
                    <VolumeControl volume={volume} preset={preset} setVolume={setVolume} setPreset={setPreset} theme="dark" />
                </div>
                {atEnd && (
                    <p className="text-center mt-4 text-sm text-white/60" data-testid="player-finished">
                        That's the whole story, for today. <Link to={`/profiles/${id}/reminisce`} className="underline">Play it again</Link>
                    </p>
                )}
            </div>
        </div>
    );
}
